import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import CreateTenderingContext, {
  TENDERING_CREATE_STEPS,
} from "./CreateTenderingContext";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import Stepper from "react-stepper-horizontal";
import SelectType from "./SelectType";
import DesignInfomation from "./Design/DesignInfomation";
import _ from "lodash";
import DesignImages from "./Design/DesignImages";
import DesignSummary from "./Design/DesignSummary";
import DesignDocuments from "./Design/DesignDocuments";
import useTendering from "../../../hooks/useTendering";
import { toast } from "react-toastify";
import ConstructionInfomation from "./Construction/ConstructionInfomation";
import ConstructionImages from "./Construction/ConstructionImages";
import ConstructionDocuments from "./Construction/ConstructionDocuments";
import ConstructionSummary from "./Construction/ConstructionSummary";

const assetsUri = import.meta.env.ASSETS_URI;

const Create = (props) => {
  const { tenderingId } = useParams();
  const [init, setInit] = useState(undefined);
  const [details, setDetails] = useState({});
  const [currentStep, setCurrentStep] = useState(
    tenderingId ? TENDERING_CREATE_STEPS.INFO : TENDERING_CREATE_STEPS.SELECT
  );
  const { getById } = useTendering();

  const navigate = useNavigate();

  const _getPath = () => (details.type === 1 ? "design" : "construction");

  const handleGoBackward = () => {
    if (currentStep === TENDERING_CREATE_STEPS.INFO && !tenderingId) {
      navigate("./select-type");
      return;
    }

    if (currentStep === TENDERING_CREATE_STEPS.INFO && tenderingId) {
      navigate("/profile");
      return;
    }

    if (currentStep === TENDERING_CREATE_STEPS.IMAGES) {
      navigate(`./${_getPath()}/info`);
      return;
    }

    if (currentStep === TENDERING_CREATE_STEPS.DOCUMENTS) {
      navigate(`./${_getPath()}/images`);
      return;
    }

    if (currentStep === TENDERING_CREATE_STEPS.SUMMARY) {
      navigate(`./${_getPath()}/documents`);
      return;
    }
  };

  const handleGoForward = () => {
    if (currentStep === TENDERING_CREATE_STEPS.SELECT) {
      navigate(`./${_getPath()}/info`);
      return;
    }

    if (currentStep === TENDERING_CREATE_STEPS.INFO) {
      navigate(`./${_getPath()}/images`);
      return;
    }

    if (currentStep === TENDERING_CREATE_STEPS.IMAGES) {
      navigate(`./${_getPath()}/documents`);
      return;
    }

    if (currentStep === TENDERING_CREATE_STEPS.DOCUMENTS) {
      navigate(`./${_getPath()}/summary`);
      return;
    }
  };

  const contextValue = useMemo(
    () => ({
      STEPS: TENDERING_CREATE_STEPS,
      edit: tenderingId,
      goBackward: handleGoBackward,
      goForward: handleGoForward,
      details,
      setDetails,
      currentStep,
      setCurrentStep,
    }),
    [tenderingId, details, currentStep]
  );

  useEffect(() => {
    if (!tenderingId) return;

    getById(tenderingId)
      .then((resp) => {
        const {
          name,
          desc,
          category,
          period,
          work,
          space,
          styles,
          location,
          googleMap,
          duration,
          budget,
          bankGuarantee,
          taxRequirement,
          images: { cover, gallery, moodAndTones, renderings },
          documents: { drawings, miscellaneous, perspectiveRenders },
          area,
          retention,
        } = resp;

        const _mapImage = (imageObject) => {
          return {
            _id: imageObject._id,
            dataUrl: `${assetsUri}/${imageObject.original}`,
            file: {
              name: imageObject.name,
              type: imageObject.mimeType,
            },
            crop: imageObject.cropPosition,
          };
        };

        const _mapDocuments = (docObject) => {
          return {
            _id: docObject._id,
            file: {
              name: docObject.name,
              type: docObject.mimeType,
              size: docObject.size,
            },
          };
        };

        setDetails({
          _id: tenderingId,
          type: category,
          info: {
            name,
            desc,
            startDate: period?.start,
            endDate: period?.end,
            typeOfWork: work,
            typeOfSpace: space,
            typeOfStyles: styles,
            location,
            googleMap,
            duration: duration.value,
            durationUnit: duration.unit,
            areaSize: area.size,
            areaUnit: area.unit,
            budget,
            bankGuarantee: bankGuarantee?.toString() ?? "",
            taxRequirement,
            retention,
          },
          images: {
            cover: _mapImage(cover),
            gallery: gallery.map((i) => _mapImage(i)),
            moodAndTones: moodAndTones.map((i) => _mapImage(i)),
            renderings: renderings.map((i) => _mapImage(i)),
          },
          files: {
            drawings: drawings.map((i) => _mapDocuments(i)),
            miscellaneous: miscellaneous.map((i) => _mapDocuments(i)),
            perspectiveRenders: perspectiveRenders.map((i) => _mapDocuments(i)),
          },
        });

        if (category === 1) {
          navigate("design/info");
        }

        if (category === 2) {
          navigate("construction/info");
        }
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.msg ??
            err.message ??
            "Unable to retrieve project information"
        );
        // FIXME: Uncomment me
        // navigate("/404");
      });
  }, [tenderingId]);

  return (
    <CreateTenderingContext.Provider value={contextValue}>
      <div className="tendering-create">
        <div className="tendering-create__stepper">
          {currentStep > TENDERING_CREATE_STEPS.SELECT && (
            <Stepper
              steps={[
                { title: "Information" },
                { title: "Images" },
                { title: "Documents" },
                { title: "Summary" },
              ]}
              activeStep={currentStep - 2}
              activeColor="#013565"
              completeColor="#013565"
              activeTitleColor="#013565"
              completeBarColor="#013565"
              completeTitleColor="#013565"
              defaultColor="#BEBEBE"
              defaultBarColor="#BEBEBE"
              defaultTitleColor="#9D9D9D"
              circleFontSize={16}
              titleFontSize={12}
              titleTop={8}
              lineMarginOffset={0}
            />
          )}
        </div>
        <Routes>
          <Route path="select-type" element={<SelectType />} />
          {!_.isEmpty(details) && (
            <>
              <Route path="design">
                <Route path="info" element={<DesignInfomation />} />
                <Route path="images" element={<DesignImages />} />
                <Route path="documents" element={<DesignDocuments />} />
                <Route path="summary" element={<DesignSummary />} />
              </Route>
              <Route path="construction">
                <Route path="info" element={<ConstructionInfomation />} />
                <Route path="images" element={<ConstructionImages />} />
                <Route path="documents" element={<ConstructionDocuments />} />
                <Route path="summary" element={<ConstructionSummary />} />
              </Route>
            </>
          )}
          <Route path="*" element={<SelectType />} />
        </Routes>
      </div>
    </CreateTenderingContext.Provider>
  );
};

Create.propTypes = {};

export default Create;
